import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AddressGroup from 'components/field/AddressGroup';
import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import SwitchInput from 'components/field/SwitchInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import Image from 'components/global/Image';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { AUTOCOMPLETE, ENUMS, PRICE_CONFIGURATIONS, SHIPPING_PRICES } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const defaultColSpan = {
	md: 6
};

const AddPriceForm = NiceModal.create(({ id, modalName, mode = 'add', deliveryCompanySettingsID, currency }) => {
	const modal = useModal();
	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const { closeModal } = useModalRouter();
	const { Country, Currency: defaultCurrency } = useLocale();

	const isViewMode = useMemo(() => mode === 'view', [mode]);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					deliveryCompanySettingsId: yup.number().nullable().required(),

					originAddress: yup.object().shape({
						countryName: yup.string().nullable(),
						cityName: yup.string().nullable()
					}),
					destinationAddress: yup.object().shape({
						countryName: yup.string().nullable(),
						cityName: yup.string().nullable()
					}),
					originTier: yup.string().nullable(),
					destinationTier: yup.string().nullable(),
					isSameCity: yup.boolean().nullable(),
					isBetweenCity: yup.boolean().nullable(),
					greaterThanKm: yup.number().nullable(),
					lessThanOrEqualKm: yup.number().nullable(),
					greaterThanWeight: yup.number().nullable(),
					lessThanOrEqualWeight: yup.number().nullable(),
					returnOrderFee: yup.number().nullable(),
					maxFreeWeight: yup.number().nullable(),
					extraWeightPerKg: yup.number().nullable(),
					maxFreeKm: yup.number().nullable(),
					extraChargePerKm: yup.number().nullable(),
					applyCodRateAfter: yup.number().nullable(),
					codPercentage: yup.number().nullable(),
					codCharge: yup.number().nullable(),
					basePrice: yup.number().nullable().required()
				})
				.required(),
		[]
	);

	const fillForm = async () => {
		const defaultValues = {
			currency,
			deliveryCompanySettingsId: deliveryCompanySettingsID,
			deliveryCompanySettingsName: null,
			originAddress: {
				countryName: Country,
				cityName: null,
				countryID: null,
				cityID: null
			},
			destinationAddress: {
				countryName: Country,
				cityName: null,
				countryID: null,
				cityID: null
			},
			originTier: null,
			destinationTier: null,
			isSameCity: false,
			isBetweenCity: false,
			greaterThanKm: null,
			lessThanOrEqualKm: null,
			greaterThanWeight: null,
			lessThanOrEqualWeight: null,
			returnOrderFee: null,
			maxFreeWeight: null,
			extraWeightPerKg: null,
			maxFreeKm: null,
			extraChargePerKm: null,
			applyCodRateAfter: null,
			codPercentage: null,
			codCharge: null,
			basePrice: null
		};

		if (!id) {
			return defaultValues;
		}

		try {
			const { data, status } = await axiosPrivate.post(PRICE_CONFIGURATIONS.GET_SPECIAL_CONTRACT_TERM, { id });
			if (status === 200) {
				return {
					...defaultValues,
					...data,
					deliveryCompanySettingsName: data?.shippingContract?.name ?? null,
					deliveryCompanySettingsId: data?.shippingContract?.deliveryCompanySettingsId ?? null,
					originAddress: {
						countryName: data?.originCountry ?? Country,
						cityName: data?.originCity ?? null
					},
					destinationAddress: {
						countryName: data?.destinationCountry ?? Country,
						cityName: data?.destinationCity ?? null
					}
				};
			} else {
				throw new Error(data);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const isLoading = isFormLoading || isSubmitting;

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const onSubmit = async (data) => {
		const url = SHIPPING_PRICES.ADD_SHIPPING_PRICE_SPECIAL_SETTING;
		await axiosPrivate
			.post(url, {
				id: id ?? null,
				deliveryCompanySettingsId: data.deliveryCompanySettingsId,
				deliveryCompanySettingsName: data.deliveryCompanySettingsName,
				currency: data.currency,
				destinationCity: data.destinationAddress.cityName,
				destinationCountry: data.destinationAddress.countryName,
				originCity: data.originAddress.cityName,
				originCountry: data.originAddress.countryName,
				originTier: data.originTier,
				destinationTier: data.destinationTier,
				isSameCity: data.isSameCity,
				isBetweenCity: data.isBetweenCity,
				greaterThanKm: data.greaterThanKm,
				lessThanOrEqualKm: data.lessThanOrEqualKm,
				greaterThanWeight: data.greaterThanWeight,
				lessThanOrEqualWeight: data.lessThanOrEqualWeight,
				returnOrderFee: data.returnOrderFee,
				maxFreeWeight: data.maxFreeWeight,
				extraWeightPerKg: data.extraWeightPerKg,
				maxFreeKm: data.maxFreeKm,
				extraChargePerKm: data.extraChargePerKm,
				applyCodRateAfter: data.applyCodRateAfter,
				codPercentage: data.codPercentage,
				codCharge: data.codCharge,
				basePrice: data.basePrice
			})
			.then((response) => {
				const { status } = response;

				if (status === 200) {
					dispatch(updateDynamicListData(DYNAMICLISTS.SHIPPING_PRICES));
					showSuccess();
					handleCloseModal();
				}
			})
			.catch((error) => {
				showError({
					message: error?.response?.data?.code || error?.response?.data?.message
				});
				AppLogger.error(error);
			});
	};

	const deliveryCompaniesColumns = useMemo(
		() => [
			{
				accessor: 'logo',
				render: (logo) => <Image url={logo} alt="logo" width="25px" height="25px" />,
				width: 50
			},
			{ accessor: 'Name' },
			{ accessor: 'integrationName' }
		],
		[]
	);

	return (
		<Modal fullwidth name={modalName} maxWidth="medium" className="modal-specialPriceSettingForm">
			<ModalTitle>
				<Typography variant="p1">addPrice</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="deliveryCompany" isLoading={isLoading} required>
						<AutoComplete
							name="deliveryCompanySettingsId"
							autoCompleteName={AUTOCOMPLETE.DELIVERY_COMPANY_SETTINGS_ACTIVE_PASSIVE}
							columns={deliveryCompaniesColumns}
							selectionLabelAccessor={['Name']}
							disabled={mode !== 'add'}
							value={watch('deliveryCompanySettingsId')}
							setValue={(value) => {
								setValue(`deliveryCompanySettingsId`, value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
								setValue(`deliveryCompanySettingsName`, value?.Name ?? null);
							}}
						/>
					</FormItem>

					<Alert content="replacementConditions.category" className="m-3 mt-5" showIcon />

					<AddressGroup
						disabled={isViewMode}
						formFieldName="originAddress"
						isLoading={isLoading}
						labels={{
							country: 'originCountry',
							city: 'originCity'
						}}
						fieldsColspan={{
							address: 0,
							country: defaultColSpan,
							state: 0,
							city: defaultColSpan,
							district: 0,
							zipCode: 0,
							lat: 0,
							lng: 0
						}}
						requiredFields={{
							country: false,
							city: false
						}}
						customTextFields={{
							country: false,
							state: true,
							city: false,
							district: true
						}}
					/>

					<AddressGroup
						disabled={isViewMode}
						formFieldName="destinationAddress"
						isLoading={isLoading}
						labels={{
							country: 'destinationCountry',
							city: 'destinationCity'
						}}
						fieldsColspan={{
							address: 0,
							country: defaultColSpan,
							state: 0,
							city: defaultColSpan,
							district: 0,
							zipCode: 0,
							lat: 0,
							lng: 0
						}}
						requiredFields={{
							country: false,
							city: false
						}}
						customTextFields={{
							country: false,
							state: true,
							city: false,
							district: true
						}}
					/>

					<FormItem colSpan={defaultColSpan} label="originTier" isLoading={isLoading}>
						<EnumSelect
							disabled={isViewMode}
							searchable={false}
							name="originTier"
							enumName={ENUMS.PRICE_CONFIGURATION_TIER_TYPE}
							setValue={(value) => {
								setValue(`originTier`, value ?? null, { shouldValidate: true, shouldDirty: true });
							}}
							value={watch(`originTier`)}
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="destinationTier" isLoading={isLoading}>
						<EnumSelect
							disabled={isViewMode}
							searchable={false}
							name="destinationTier"
							enumName={ENUMS.PRICE_CONFIGURATION_TIER_TYPE}
							setValue={(value) => {
								setValue(`destinationTier`, value ?? null, { shouldValidate: true, shouldDirty: true });
							}}
							value={watch(`destinationTier`)}
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="isSameCity" isLoading={isLoading}>
						<SwitchInput
							disabled={isViewMode}
							className="mt-3"
							value={watch('isSameCity')}
							setValue={(event) => {
								const { checked: value } = event.target;
								setValue(`isSameCity`, value, { shouldValidate: true, shouldDirty: true });

								if (value) {
									setValue(`isBetweenCity`, false, { shouldValidate: true, shouldDirty: true });
								}
							}}
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="isBetweenCity" isLoading={isLoading}>
						<SwitchInput
							disabled={isViewMode}
							className="mt-3"
							value={watch('isBetweenCity')}
							setValue={(event) => {
								const { checked: value } = event.target;

								setValue(`isBetweenCity`, value, { shouldValidate: true, shouldDirty: true });

								if (value) {
									setValue(`isSameCity`, false, { shouldValidate: true, shouldDirty: true });
								}
							}}
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="greaterThanKm" isLoading={isLoading}>
						<IntegerInput name="greaterThanKm" allowFloat decimalLimit={2} disabled={isViewMode} />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="lessThanOrEqualKm" isLoading={isLoading}>
						<IntegerInput name="lessThanOrEqualKm" allowFloat decimalLimit={2} disabled={isViewMode} />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="greaterThanWeight" isLoading={isLoading}>
						<IntegerInput name="greaterThanWeight" allowFloat decimalLimit={2} disabled={isViewMode} />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="lessThanOrEqualWeight" isLoading={isLoading}>
						<IntegerInput name="lessThanOrEqualWeight" allowFloat decimalLimit={2} disabled={isViewMode} />
					</FormItem>

					<Alert content="additionalPrices" className="m-3 mt-5" showIcon />

					<FormItem colSpan={defaultColSpan} label="maxFreeKm" isLoading={isLoading}>
						<IntegerInput name="maxFreeKm" allowFloat decimalLimit={2} disabled={isViewMode} />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="extraFeePerKm" isLoading={isLoading}>
						<IntegerInput
							name="extraChargePerKm"
							allowFloat
							disabled={isViewMode}
							decimalLimit={2}
							unit={currency || defaultCurrency}
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="maxFreeWeight" isLoading={isLoading}>
						<IntegerInput
							name="maxFreeWeight"
							allowFloat
							decimalLimit={2}
							unit={'kg'}
							disabled={isViewMode}
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="extraFeePerKg" isLoading={isLoading}>
						<IntegerInput
							name="extraWeightPerKg"
							allowFloat
							disabled={isViewMode}
							decimalLimit={2}
							unit={currency || defaultCurrency}
						/>
					</FormItem>

					<Alert content="CODFees" className="m-3 mt-5" showIcon />

					<FormItem colSpan={defaultColSpan} label="codCharge" isLoading={isLoading}>
						<IntegerInput
							name="codCharge"
							allowFloat
							disabled={isViewMode}
							decimalLimit={2}
							unit={currency || defaultCurrency}
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="codPercentage" isLoading={isLoading}>
						<IntegerInput name="codPercentage" allowFloat decimalLimit={2} disabled={isViewMode} />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="applyCodRateAfter" isLoading={isLoading}>
						<IntegerInput
							name="applyCodRateAfter"
							allowFloat
							disabled={isViewMode}
							decimalLimit={2}
							unit={currency || defaultCurrency}
						/>
					</FormItem>

					<Alert content="shippingPrices" className="m-3 mt-5" showIcon />

					<FormItem colSpan={defaultColSpan} label="deliveryPrice" isLoading={isLoading} required>
						<IntegerInput
							name="basePrice"
							allowFloat
							disabled={isViewMode}
							decimalLimit={2}
							unit={currency || defaultCurrency}
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="returnOrderFee" isLoading={isLoading}>
						<IntegerInput
							name="returnOrderFee"
							allowFloat
							disabled={isViewMode}
							decimalLimit={2}
							unit={currency || defaultCurrency}
						/>
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label={'cancel'} variant="outline" onClick={handleCloseModal} />
				{!isViewMode && (
					<Button
						loading={isLoading}
						label={mode === 'edit' ? 'save' : 'add'}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default AddPriceForm;
